<script setup lang="ts">
import { ref, computed } from 'vue'

const name = ref('')
const email = ref('')
const message = ref('')
const isSending = ref(false)
const sent = ref(false)
const error = ref<string | null>(null)

const isValid = computed(() =>
  name.value.trim().length > 0 &&
  email.value.trim().length > 0 &&
  email.value.includes('@') &&
  message.value.trim().length > 0
)

async function submit() {
  if (!isValid.value) return

  isSending.value = true
  error.value = null

  try {
    const res = await fetch('/TecDocContact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fullName: name.value,
        email: email.value,
        enquiry: message.value,
      }),
    })

    if (res.ok) {
      sent.value = true
    } else {
      error.value = 'Kunne ikke sende melding. Prøv igjen.'
    }
  } catch {
    error.value = 'Nettverksfeil. Sjekk tilkoblingen.'
  } finally {
    isSending.value = false
  }
}
</script>

<template>
  <div class="contact-form">
    <template v-if="sent">
      <div class="contact-form__success">
        <p>Takk for din henvendelse! Vi tar kontakt snart.</p>
      </div>
    </template>

    <template v-else>
      <h4>Fant du ikke det du lette etter?</h4>
      <p>Send oss en melding, så hjelper vi deg.</p>

      <form @submit.prevent="submit" class="contact-form__fields">
        <input
          v-model="name"
          type="text"
          placeholder="Fullt navn"
          class="contact-form__input"
          required
        />
        <input
          v-model="email"
          type="email"
          placeholder="E-post"
          class="contact-form__input"
          required
        />
        <textarea
          v-model="message"
          placeholder="Hva leter du etter?"
          class="contact-form__textarea"
          rows="3"
          required
        ></textarea>

        <div v-if="error" class="contact-form__error">{{ error }}</div>

        <button
          type="submit"
          class="contact-form__btn"
          :disabled="!isValid || isSending"
        >
          {{ isSending ? 'Sender...' : 'Send melding' }}
        </button>
      </form>
    </template>
  </div>
</template>

<style scoped lang="scss">
.contact-form {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f9fafb;
  border-radius: 8px;
  max-width: 500px;

  h4 { margin-bottom: 0.25rem; }
  p { font-size: 0.9rem; color: #666; margin-bottom: 1rem; }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  &__input, &__textarea {
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    width: 100%;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #002868;
    }
  }

  &__btn {
    background: #002868;
    color: white;
    border: none;
    padding: 10px 24px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
    align-self: flex-start;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &:hover:not(:disabled) { background: #001d4f; }
  }

  &__error {
    color: #dc2626;
    font-size: 0.85rem;
  }

  &__success {
    text-align: center;
    padding: 1rem;
    background: #ecfdf5;
    border-radius: 6px;
    color: #065f46;
  }
}
</style>
